import { useEffect, useMemo, useState } from "react";
import { AdminRecord, getAdmins, GetAdminsParams } from "../admins";
import { getApiErrorMessage } from "../../lib";
import { PaginationMeta } from "../../lib/bufferedPagination";

type UseAdminsState = {
  admins: AdminRecord[];
  meta?: PaginationMeta;
  isLoading: boolean;
  error: string | null;
};

const adminsCache = new Map<string, { items: AdminRecord[]; meta?: PaginationMeta }>();

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - 2);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

export const useAdmins = (params?: Partial<GetAdminsParams>) => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const requestParams: GetAdminsParams = useMemo(() => ({
    startDate: params?.startDate ?? defaultRange.startDate,
    endDate: params?.endDate ?? defaultRange.endDate,
    limit: params?.limit ?? 100,
    sort: params?.sort ?? "a-z",
    q: params?.q ?? "",
    page: params?.page ?? 1,
    roleIds: params?.roleIds,
    isProfileCompleted: params?.isProfileCompleted,
  }), [
    defaultRange.endDate,
    defaultRange.startDate,
    params?.endDate,
    params?.isProfileCompleted,
    params?.limit,
    params?.page,
    params?.q,
    params?.roleIds,
    params?.sort,
    params?.startDate,
  ]);
  const requestKey = JSON.stringify(requestParams);
  const [state, setState] = useState<UseAdminsState>({
    admins: [],
    meta: undefined,
    isLoading: true,
    error: null,
  });
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = adminsCache.get(requestKey);
      setState((prev) => ({
        admins: cached?.items ?? prev.admins,
        meta: cached?.meta ?? prev.meta,
        isLoading: cached ? false : true,
        error: null,
      }));
      try {
        const adminsResult = await getAdmins(requestParams);
        if (!isMounted) return;
        adminsCache.set(requestKey, adminsResult);
        setState({
          admins: adminsResult.items,
          meta: adminsResult.meta,
          isLoading: false,
          error: null,
        });

        const nextParams = {
          ...requestParams,
          page: (requestParams.page ?? 1) + 1,
        };
        const nextKey = JSON.stringify(nextParams);
        if (!adminsCache.has(nextKey)) {
          getAdmins(nextParams)
            .then((nextResult) => {
              adminsCache.set(nextKey, nextResult);
            })
            .catch(() => {});
        }
      } catch (error) {
        if (!isMounted) return;
        const message = getApiErrorMessage(error, "Unable to load members");
        setState({ admins: [], meta: undefined, isLoading: false, error: message });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [refreshCount, requestKey, requestParams]);

  return {
    ...state,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
